import { useMemo, useState } from "react";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { usePushSubscription } from "@/lib/push";
import {
  fetchPublicVapidKey,
  subscribePublicPush,
  unsubscribePublicPush,
} from "@/services/publicBillApi";

/**
 * Opt-in push for this friend on this bill. Silent where push can't work
 * (LINE's in-app browser, unsupported browsers); on iOS it explains the
 * Home Screen requirement instead.
 */
export default function NotifyCard({
  token,
  personId,
}: {
  token: string;
  personId: string;
}) {
  const binding = useMemo(
    () => ({
      fetchKey: fetchPublicVapidKey,
      register: (sub: Parameters<typeof subscribePublicPush>[2]) =>
        subscribePublicPush(token, personId, sub),
      unregister: (endpoint: string) =>
        unsubscribePublicPush(token, personId, endpoint),
    }),
    [token, personId],
  );
  const { state, enable, disable } = usePushSubscription(binding);
  const [busy, setBusy] = useState(false);

  if (
    state === "loading" ||
    state === "unsupported" ||
    state === "unavailable"
  ) {
    return null;
  }

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-border bg-card p-3">
      <BellRing className="mt-0.5 w-4 h-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 text-xs text-muted-foreground">
        {state === "needs-install" && (
          <>
            Want a ping when this bill is ready to pay? Tap Share, then{" "}
            <b className="text-foreground">Add to Home Screen</b>, and open it
            from there.
          </>
        )}
        {state === "off" && "Get a notification when the bill is ready to pay."}
        {state === "on" && "Notifications are on for this bill."}
        {state === "blocked" &&
          "Notifications are blocked. Allow them in your browser's site settings."}
      </div>
      {state === "off" && (
        <Button size="sm" disabled={busy} onClick={() => run(enable)}>
          Notify me
        </Button>
      )}
      {state === "on" && (
        <Button
          size="sm"
          variant="ghost"
          disabled={busy}
          onClick={() => run(disable)}
        >
          Turn off
        </Button>
      )}
    </div>
  );
}
